import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";
import { withFollow } from "./followHelpers";

export const listBankTransfersSkill: Skill = {
  id: "listBankTransfers",
  description:
    "Lista los intermediarios bancarios (Nequi, Daviplata, Bancolombia, etc.) configurados para recibir pagos por transferencia.",
  schema: {
    type: "object",
    properties: { restaurantId: { type: "string" } },
    required: ["restaurantId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    if (!restaurantId) return { ok: false, error: "Falta restaurantId." };
    return withFollow(ctx, {
      intro: "Listando intermediarios…",
      successMsg: "✓ Intermediarios obtenidos",
      run: async () => {
        try {
          const { data } = await apiWithAuth.get(
            `/restaurants/${restaurantId}/bank-transfers`,
            ctx.accessToken,
          );
          const items: any[] = data?.items ?? data?.data ?? data ?? [];
          return {
            ok: true as const,
            result: {
              count: items.length,
              transfers: items.map((t) => ({
                id: t.id,
                name: t.name,
                details: t.details,
                isActive: t.isActive,
              })),
            },
          };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const createBankTransferSkill: Skill = {
  id: "createBankTransfer",
  description: "Crea un nuevo intermediario bancario para recibir transferencias.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      name: { type: "string" },
      details: { type: "string" },
    },
    required: ["restaurantId", "name"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const name = String(args.name ?? "").trim();
    if (!restaurantId || !name) return { ok: false, error: "Faltan datos." };
    const details = args.details ? String(args.details) : undefined;

    const ok = await ctx.confirmation.confirm({
      title: "Crear intermediario bancario",
      details: [
        { label: "Nombre", value: name },
        ...(details ? [{ label: "Detalles", value: details }] : []),
      ],
      confirmLabel: "Crear",
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: `Creando intermediario "${name}"…`,
      successMsg: `✓ "${name}" creado`,
      restaurantId,
      targetTab: "intermediarios",
      targetTabLabel: "Intermediarios",
      run: async () => {
        try {
          const body: Record<string, unknown> = { name };
          if (details) body.details = details;
          const { data } = await apiWithAuth.post(
            `/restaurants/${restaurantId}/bank-transfers`,
            body,
            ctx.accessToken,
          );
          return {
            ok: true as const,
            result: { id: data?.id, name },
            humanSummary: `Intermediario "${name}" creado.`,
          };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const updateBankTransferSkill: Skill = {
  id: "updateBankTransfer",
  description: "Actualiza un intermediario bancario.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      transferId: { type: "string" },
      name: { type: "string" },
      details: { type: "string" },
      isActive: { type: "boolean" },
    },
    required: ["restaurantId", "transferId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const transferId = String(args.transferId ?? "");
    if (!restaurantId || !transferId) return { ok: false, error: "Faltan IDs." };
    const patch: Record<string, unknown> = {};
    const details: Array<{ label: string; value: string }> = [];
    if (args.name) { patch.name = String(args.name); details.push({ label: "Nombre", value: patch.name as string }); }
    if (args.details !== undefined) { patch.details = String(args.details); details.push({ label: "Detalles", value: patch.details as string }); }
    if (typeof args.isActive === "boolean") { patch.isActive = args.isActive; details.push({ label: "Activo", value: args.isActive ? "Sí" : "No" }); }
    if (Object.keys(patch).length === 0) return { ok: false, error: "Nada que actualizar." };

    const ok = await ctx.confirmation.confirm({ title: "Actualizar intermediario", details, confirmLabel: "Actualizar" });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Actualizando intermediario…",
      successMsg: "✓ Intermediario actualizado",
      restaurantId,
      targetTab: "intermediarios",
      targetTabLabel: "Intermediarios",
      run: async () => {
        try {
          await apiWithAuth.patch(
            `/restaurants/${restaurantId}/bank-transfers/${transferId}`,
            patch,
            ctx.accessToken,
          );
          return { ok: true as const, result: { transferId, ...patch }, humanSummary: "Intermediario actualizado." };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const deleteBankTransferSkill: Skill = {
  id: "deleteBankTransfer",
  description: "Elimina un intermediario bancario. IRREVERSIBLE.",
  schema: {
    type: "object",
    properties: { restaurantId: { type: "string" }, transferId: { type: "string" } },
    required: ["restaurantId", "transferId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const transferId = String(args.transferId ?? "");
    if (!restaurantId || !transferId) return { ok: false, error: "Faltan IDs." };

    const ok = await ctx.confirmation.confirm({
      title: "Eliminar intermediario",
      description: "Acción irreversible. No se podrá recibir transferencias por este medio.",
      confirmLabel: "Eliminar",
      danger: true,
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Eliminando intermediario…",
      successMsg: "✓ Intermediario eliminado",
      restaurantId,
      targetTab: "intermediarios",
      targetTabLabel: "Intermediarios",
      run: async () => {
        try {
          await apiWithAuth.delete(
            `/restaurants/${restaurantId}/bank-transfers/${transferId}`,
            ctx.accessToken,
          );
          return { ok: true as const, result: { transferId }, humanSummary: "Intermediario eliminado." };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};
